 
import React from "react";
import { PageLoader } from "../../../../../../../components/PageLoader/PageLoader";
import { StandadCard } from "../../../../../../../components/Cards";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../../redux/initialStoreState";
import { MODULE_IDS } from "../../../../../../../constants/enums";
import { useDispatchWrapper } from "../../../../../../../hooks";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import {
  Box,
  Checkbox,
  Divider,
  Grid,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomShrinkTextField,
  CustomTextField,
} from "../../../../../../../components/formsComponents";

import produce from "immer";
import { clearAutomobileScheduleState } from "../../../../../../../redux/automobileSchedule/automobileScheduleActions";

import { AutoComplete } from "../../../../../../../components/AutoCompleteSearches/AutoComplete";
import {
  AUTOMOBILE_CSIO_COVERAGE,
  COMMON_STATUS,
  csioOtherCoverages,
} from "../../../../../../../constants/constants";
import {
  addCommAutomobileScheduleAAsync,
  fetchCommAutomobileScheduleAAsync,
} from "../../../../../../../redux/commAutoPolicy/commAutoSchedule/commAutoScheduleActions";
import { RoleBasedCustomButton } from "../../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { fetchCommAutomobileVehicleListAsync } from "../../../../../../../redux/commAutoPolicy/commAutoVehicle/commAutoVehicleActions";
import { ICommAutomobileScheduleA } from "../../../../../../../redux/commAutoPolicy/commAutoSchedule/commAutoSchedule.types";
import { FieldSet } from "../../../../../../../components/FieldSet/FieldSet";
import { useDefaultQuote } from "../../../../../../../hooks/useDefaultQuote";
import { useCommAutoVehcileInfo } from "../../hooks/useCommAutovehicleInfo";
import { PageContainer } from "../../../../../../../components/container/PageContainer";

interface ICommAutoScheduleAProps {
  customerId?: number;
  customerPolicyId?: number;
  /**
   * Feature: lead-commercial-auto-risk-profile
   * Optional lead-scoped identifier. When supplied the coverage is saved keyed
   * by `lead_id` (via `addCommAutomobileScheduleAAsync`'s optional `leadId` arg)
   * and fetched by `lead_id`. Absent a `leadId` the form behaves exactly as
   * before (gated on / keyed by `customer_policy_id`).
   */
  leadId?: string;
  incrementalNumber?: number;
  readOnly?: boolean;
  fromRightPanel?: boolean;
  staticStatusValue?: string;
  created_from?: string;
  onSaveSuccess: (data: ICommAutomobileScheduleA) => void;
  initialData?: ICommAutomobileScheduleA;
  disableApiCalls?: boolean;
  vehicleNumber?: number;
  isPolicyChange?: boolean;
  isVehicleEnabled?: boolean;
  disableSaveApi?: boolean;
  hideDefaultCoverage?: boolean;
  renderBeforeSave?: React.ReactNode;
  renderAfterSave?: React.ReactNode;
}

export const CommAutomobileScheduleA: React.FC<ICommAutoScheduleAProps> = (
  props
) => {
  const {
    customerId,
    customerPolicyId,
    leadId,
    incrementalNumber,
    onSaveSuccess,
    fromRightPanel = false,
    readOnly = false,
    staticStatusValue,
    created_from,
    initialData,
    disableApiCalls = false,
    vehicleNumber,
    isVehicleEnabled = true,
    isPolicyChange = false,
    disableSaveApi,
    hideDefaultCoverage = false,
    renderBeforeSave,
    renderAfterSave,
  } = props;

  const { data, loading } = useSelector(
    (storeState: IStoreState) => storeState.commAuto.schedule.scheduleA
  );

  const isUpdate = incrementalNumber ? true : false;

  const dispatch = useDispatchWrapper();
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [manual, setManual] = React.useState(false);
  const { coverageList, fetchDefaultCoverageByCode } = useDefaultQuote();
  const navigate = useNavigate();

  const { vehcileDropdown, vehiclesList } = useCommAutoVehcileInfo(
    customerPolicyId,
    leadId
  );

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
  } = useFormik({
    initialValues: initialData ? initialData : data,
    validate: (values) => {
      const errors: any = {};
      if (isVehicleEnabled && !values.vehicle_number) {
        errors.vehicle_number = "Vehicle is required.";
      }
      return errors;
    },
    onSubmit: (values) => {
      if (disableSaveApi) {
        onSaveSuccess(values);
      } else if (leadId) {
        // Lead scope: persist keyed by `lead_id`. A lead may not be converted
        // to a customer yet, so `customerId` / `customerPolicyId` can be
        // undefined here — that is valid. We must NOT gate on them (doing so
        // silently no-ops the save). `buildBodyKey` nulls out
        // `customer_policy_id` and sets `lead_id`, so we do not send
        // `customer_policy_id` here.
        let statusValue = values.status;
        if (staticStatusValue) {
          statusValue = staticStatusValue;
        }
        setSaveLoading(true);
        dispatch(
          addCommAutomobileScheduleAAsync(
            {
              ...values,
              status: statusValue,
              created_from: created_from || "Coverage",
            },
            (isSuccess, data) => {
              if (isSuccess && data) {
                onSaveSuccess(data);
                window.scrollTo(0, 0);
              }
              setSaveLoading(false);
            },
            leadId
          )
        );
      } else {
        if (customerPolicyId && customerId) {
          let statusValue = values.status;
          if (staticStatusValue) {
            statusValue = staticStatusValue;
          }
          setSaveLoading(true);
          dispatch(
            addCommAutomobileScheduleAAsync(
              {
                ...values,
                customer_policy_id: customerPolicyId,
                status: statusValue,
                created_from: created_from || "Coverage",
              },
              (isSuccess, data) => {
                if (isSuccess && data) {
                  onSaveSuccess(data);
                  window.scrollTo(0, 0);
                }
                setSaveLoading(false);
              }
            )
          );
        }
      }
    },
  });

  const handleTextChange =
    (index: number, key: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValues = produce(values, (draftValues) => {
        draftValues.coverage_details[index][key as "key"] = e.target.value;
      });
      setValues(newValues);
    };

  const handleSelectChange =
    (index: number, key: string) => (selectedOption: any) => {
      const newValues = produce(values, (draftValues) => {
        draftValues.coverage_details[index][key as "key"] = selectedOption
          ? selectedOption
          : "";
      });
      setValues(newValues);
    };

  const handleCoverageChange = async (e: SelectChangeEvent<unknown>) => {
    const coverageCode = e.target.value as string;

    const CoverageInfo = await fetchDefaultCoverageByCode(coverageCode);

    if (CoverageInfo) {
      const newValues = produce(values, (draftValues) => {
        draftValues.coverage_name = coverageCode;
        draftValues.coverage_details = CoverageInfo.coverage_details;
      });
      setValues(newValues);
    }
  };

  const handleCheckboxChange =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      const newValues = produce(values, (draftValues) => {
        draftValues.coverage_details[index].isDeductible = isChecked;
      });
      setValues(newValues);
    };

  React.useEffect(() => {
    if (initialData && disableApiCalls) {
      return;
    }
    setValues({ ...data });
  }, [data]);

  React.useEffect(() => {
    if (
      leadId &&
      incrementalNumber &&
      vehicleNumber &&
      !disableApiCalls
    ) {
      // Lead scope: fetch the record keyed by `lead_id`. `customerPolicyId` is
      // passed through (undefined in lead scope) but ignored for keying.
      dispatch(
        fetchCommAutomobileScheduleAAsync(
          customerPolicyId as number,
          incrementalNumber,
          vehicleNumber,
          leadId
        )
      );
    } else if (
      customerPolicyId &&
      incrementalNumber &&
      vehicleNumber &&
      !disableApiCalls
    ) {
      dispatch(
        fetchCommAutomobileScheduleAAsync(
          customerPolicyId,
          incrementalNumber,
          vehicleNumber
        )
      );
    }
  }, []);

  React.useEffect(() => {
    if (!disableApiCalls) {
      setValues({ ...data });
    }
  }, [data]);

  React.useEffect(() => {
    return () => {
      dispatch(clearAutomobileScheduleState());
    };
  }, []);

  const total = React.useMemo(() => {
    return values.coverage_details.reduce((item, currentValue) => {
      return item + (Number(currentValue.premimum) || 0);
    }, 0);
  }, [values.coverage_details]);

  const totalTwo = React.useMemo(() => {
    return values.coverage_details.reduce((item, currentValue) => {
      return item + (Number(currentValue.description) || 0);
    }, 0);
  }, [values.coverage_details]);

  const defaultCoverageList = React.useMemo(() => {
    return coverageList.map((item) => {
      return {
        label: `${item.default_coverage_name || "--"} ( ${
          item.default_coverage_code || "N/A"
        } 
          )`,
        value: item.default_coverage_code || "",
      };
    });
  }, [coverageList]);

  const handleVinNumber = async (value: string | number) => {
    const vehicleNumber =  value as number;
    const vinNumber = vehiclesList.find(
      (x) => x.incremental_number === Number(vehicleNumber)
    );

    if (vinNumber) {
      const newValues = produce(values, (draftValues) => {
        draftValues.vehicle_number = vehicleNumber;
        draftValues.vin = vinNumber?.vin || "";
      });
      setValues(newValues);
    }
    else {  const newValues = produce(values, (draftValues) => {
      draftValues.vehicle_number = vehicleNumber;
      draftValues.vin = (vehicleNumber || "").toString();
    });
    setValues(newValues);
  }
  };

  React.useEffect(() => {
    if (customerPolicyId) {
      dispatch(fetchCommAutomobileVehicleListAsync(customerPolicyId));
    }
  }, []);

  return (
    <PageContainer
       title={isUpdate ? "Edit Vehicle Coverage" : "Add Vehicle Coverage"}
  description="this is inner page"
>

    <PageLoader loading={false}>
      <StandadCard
        sx={{
          "& > .MuiBox-root:first-of-type > .MuiTypography-root:first-of-type": {
            fontSize: "1rem",
          },
        }}
        renderWithoutCard={fromRightPanel}
        heading={!fromRightPanel ? "Vehicle Coverage Summary" : ""}
        rightHeading={
          !incrementalNumber && !hideDefaultCoverage && (
            <FieldSet disabled={readOnly}>
              <Stack
                direction={"row"}
                spacing={3}
                width={"100%"}
                alignItems={"center"}
              >
                <Typography
                  display={"block"}
                  variant="h6"
                  fontWeight={"bold"}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Default Coverage{" "}
                </Typography>
                <ControlledCustomSelect
                  sx={{ width: "300px" }}
                  variant="outlined"
                  size="small"
                  type="string"
                  name="coverage_name"
                  value={values.coverage_name}
                  onChange={handleCoverageChange}
                  displayEmpty
                  fullWidth
                  placeholder="Select one"
                  options={defaultCoverageList}
                ></ControlledCustomSelect>
              </Stack>
            </FieldSet>
          )
        }
      >
        <form onSubmit={handleSubmit}>
          <FieldSet disabled={readOnly}>
            <>
              <Grid container sx={{ mt: 0 }} spacing={0.5}>
                <Grid item xs={12} md={2}>
                  <CustomFormLabel
                    fontWeight={600}
                    variant="h6"
                    textAlign={"center"}
                    sx={{ mt: 0 }}
                  >
                    Coverage #
                  </CustomFormLabel>
                  <CustomTextField
                    name="coverage_number"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    value={values.coverage_number}
                    onChange={handleChange}
                    disabled={!isPolicyChange}
                  />
                </Grid>
                {isVehicleEnabled && (
                  <Grid item xs={12} md={2}>
                    <CustomFormLabel
                      fontWeight={600}
                      variant="h6"
                      textAlign={"center"}
                      sx={{ mt: 0 }}
                    >
                      Manual Vehicle
                    </CustomFormLabel>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      <Checkbox
                        checked={manual}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setManual(isChecked);
                        }}
                      />
                    </Stack>
                  </Grid>
                )}
                {isVehicleEnabled && (
                  <Grid item xs={12} md={6}>
                    <CustomFormLabel
                      fontWeight={600}
                      variant="h6"
                      textAlign={"center"}
                      sx={{ mt: 0 }}
                    >
                      Vehicle Number
                    </CustomFormLabel>
                    {manual ? (
                      <CustomTextField
                        fullWidth
                        value={values.vehicle_number}
                        name="vehicle_number"
                        type="text"
                        onChange={(e) => handleVinNumber(e.target.value)}
                        error={errors.vehicle_number ? true : false}
                        helperText={errors.vehicle_number}
                      />
                    ) : (
                      <ControlledCustomSelect
                        variant="outlined"
                        size="small"
                        type="string"
                        name="vehicle_number"
                        value={values.vehicle_number ? String(values.vehicle_number) : ""}
                        onChange={(e) => handleVinNumber(Number(e.target.value))}
                        displayEmpty
                        fullWidth
                        placeholder="Select one"
                        options={vehcileDropdown}
                        error={errors.vehicle_number ? true : false}
                        helperText={errors.vehicle_number}
                      />
                    )}
                  </Grid>
                )}
                {!staticStatusValue && (
                  <Grid item xs={12} md={2}>
                    <CustomFormLabel
                      fontWeight={600}
                      variant="h6"
                      textAlign={"center"}
                      sx={{ mt: 0 }}
                    >
                      Status
                    </CustomFormLabel>
                    <ControlledCustomSelect
                      fullWidth
                      value={values.status}
                      name="status"
                      onChange={handleChange}
                      placeholder="Select one"
                      displayEmpty
                      options={COMMON_STATUS}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={12}>
                  <TableContainer
                    sx={{
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                    }}
                  >
                    <Table
                      size="small"
                      aria-label="collapsible table"
                      sx={{
                        whiteSpace: {
                          xs: "nowrap",
                          sm: "nowrap",
                        },
                        "& .MuiTableCell-root": {
                          py: 0.25,
                          px: 0.5,
                        },
                        "& tbody .MuiTableRow-root": {
                          backgroundColor: (theme) =>
                            theme.palette.common.white,
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow
                          sx={{
                            "& .MuiTableCell-root": {
                              backgroundColor: (theme) =>
                                theme.palette.primary.main,
                              color: (theme) =>
                                theme.palette.primary.contrastText,
                            },
                          }}
                        >
                          <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Section
                            </Typography>
                          </TableCell>
                          <TableCell align="center" colSpan={3}>
                            <Typography fontWeight={600} variant="h6">
                              Automobile
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            "& .MuiTableCell-root": {
                              backgroundColor: (theme) =>
                                theme.palette.success.main,
                              color: (theme) =>
                                theme.palette.success.contrastText,
                            },
                          }}
                        >
                          <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Liability
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Limit
                            </Typography>
                          </TableCell>
                          {/* <TableCell align="center">
                                  <Typography fontWeight={600} variant="h6">
                                    Deductible
                                  </Typography>
                                </TableCell> */}
                          <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Premium
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Occassional Driver Premium
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {values.coverage_details.map((item, index) => {
                          const showDeductibleOnly = [
                            "Specified Perils (excluding Collision or Upset)",
                            "Comprehensive (excluding Collision or Upset)",
                            "Collision or Upset",
                            "All Perils",
                            "Direct Compensation-Property Damage",
                          ].includes(item.label);

                          // Some rows come back with an empty label; fall back
                          // to the CSIO code map so the coverage name shows.
                          const csioLabel = item.code
                            ? AUTOMOBILE_CSIO_COVERAGE[`csio:${item.code}`] || ""
                            : "";

                          // Display label: strip the "Policy Change Forms" prefix
                          // and any leading dash so the coverage name reads clean.
                          const resolvedLabel = (item.label || csioLabel)
                            .replace(
                              /Policy Change Forms \(Name & No\.\)\s*/gi,
                              ""
                            )
                            .replace(/^\s*[-\u2010-\u2015]\s*/, "")
                            .trimStart();

                          // Standard CSIO coverages render as a plain label;
                          // only genuine "other" rows keep the dropdown.
                          const isKnownCoverage = Boolean(csioLabel);
                          const showAsLabel =
                            !item.isTypeOther || isKnownCoverage;

                          // Normalize (unify dashes, trim trailing punctuation,
                          // collapse spaces) so matches work regardless of
                          // en-dash vs hyphen or trailing commas.
                          const normalizeLabel = (value: string) =>
                            (value || "")
                              .replace(/[\u2010-\u2015]/g, "-")
                              .replace(/[\s,]+$/g, "")
                              .replace(/\s+/g, " ")
                              .trim()
                              .toLowerCase();

                          const isBoldLabel = [
                            "Accident Benefits (Standard Benefits)",
                            "Uninsured Automobile",
                            "Direct Compensation-Property Damage",
                          ]
                            .map(normalizeLabel)
                            .includes(normalizeLabel(resolvedLabel));

                          return (
                            <React.Fragment key={item.key}>
                              {(normalizeLabel(item.label).includes(
                                "family protection coverage"
                              ) ||
                                normalizeLabel(item.label).includes(
                                  "opcf 44r"
                                )) && (
                                <TableRow
                                  sx={{
                                    "&.MuiTableRow-root": {
                                      backgroundColor: (theme) =>
                                        theme.palette.success.main,
                                    },
                                    "& .MuiTableCell-root": {
                                      backgroundColor: (theme) =>
                                        theme.palette.success.main,
                                      color: (theme) =>
                                        theme.palette.success.contrastText,
                                    },
                                  }}
                                >
                                  <TableCell>
                                    <Typography fontWeight={600} variant="h6">
                                      Policy Change Forms (Name & No.)
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Deductible/Limit
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Premium
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Occassional Driver Premium
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}

                              {item.label ===
                                "Specified Perils (excluding Collision or Upset)" && (
                                <TableRow
                                  sx={{
                                    "&.MuiTableRow-root": {
                                      backgroundColor: (theme) =>
                                        theme.palette.success.main,
                                    },
                                    "& .MuiTableCell-root": {
                                      backgroundColor: (theme) =>
                                        theme.palette.success.main,
                                      color: (theme) =>
                                        theme.palette.success.contrastText,
                                    },
                                  }}
                                >
                                  <TableCell>
                                    <Typography fontWeight={600} variant="h6">
                                      LOSS OR DAMAGE
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Deductible
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Premium
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Occassional Driver Premium
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}

                              <TableRow
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                }}
                              >
                                <TableCell sx={{ minWidth: 300 }}>
                                  {showAsLabel ? (
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      spacing={0.5}
                                      sx={{ width: "100%" }}
                                    >
                                      <Typography
                                        fontWeight={isBoldLabel ? 700 : 400}
                                        sx={{
                                          wordBreak: "break-word",
                                          whiteSpace: "normal",
                                        }}
                                      >
                                        {resolvedLabel}
                                      </Typography>

                                      {(normalizeLabel(item.label).includes(
                                        "family protection coverage"
                                      ) ||
                                        normalizeLabel(item.label).includes(
                                          "opcf 44r"
                                        )) && (
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                        >
                                          <Typography>
                                            is deductible?
                                          </Typography>
                                          <Checkbox
                                            checked={item.isDeductible}
                                            onChange={handleCheckboxChange(
                                              index
                                            )}
                                          />
                                        </Stack>
                                      )}
                                    </Stack>
                                  ) : (
                                    <Stack
                                      direction="row"
                                      gap={2}
                                      sx={{ width: "100%", display: "flex" }}
                                    >
                                      <AutoComplete
                                        fullWidth
                                        value={resolvedLabel}
                                        placeholder="Select one"
                                        options={
                                          resolvedLabel &&
                                          !csioOtherCoverages.some(
                                            (opt) =>
                                              opt.value.toLowerCase() ===
                                              resolvedLabel.toLowerCase()
                                          )
                                            ? [
                                                {
                                                  label: resolvedLabel,
                                                  value: resolvedLabel,
                                                },
                                                ...csioOtherCoverages,
                                              ]
                                            : csioOtherCoverages
                                        }
                                        onChange={handleSelectChange(
                                          index,
                                          "label"
                                        )}
                                      />

                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                      >
                                        <Typography>is deductible?</Typography>
                                        <Checkbox
                                          checked={item.isDeductible}
                                          onChange={handleCheckboxChange(index)}
                                        />
                                      </Stack>
                                    </Stack>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <CustomShrinkTextField
                                    label={
                                      (showDeductibleOnly || item.isDeductible
                                        ? item.deductible
                                        : item.limit)
                                        ? ""
                                        : showDeductibleOnly || item.isDeductible
                                        ? "Deductible"
                                        : "Limit"
                                    }
                                    value={
                                      showDeductibleOnly || item.isDeductible
                                        ? item.deductible
                                        : item.limit
                                    }
                                    onChange={handleTextChange(
                                      index,
                                      showDeductibleOnly || item.isDeductible
                                        ? "deductible"
                                        : "limit"
                                    )}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                  />
                                </TableCell>
                                <TableCell>
                                  <CustomShrinkTextField
                                    label={item.premimum ? "" : "Premium"}
                                    value={item.premimum}
                                    onChange={handleTextChange(
                                      index,
                                      "premimum"
                                    )}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                  />
                                </TableCell>
                                <TableCell>
                                  <CustomShrinkTextField
                                    label={item.description ? "" : "Driver"}
                                    value={item.description}
                                    onChange={handleTextChange(
                                      index,
                                      "description"
                                    )}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                  />
                                </TableCell>
                              </TableRow>

                              {item.label ===
                                "Accident Benefits (Standard Benefits)" && (
                                <TableRow
                                  sx={{
                                    "&.MuiTableRow-root": {
                                      backgroundColor: (theme) =>
                                        theme.palette.success.main,
                                    },
                                    "& .MuiTableCell-root": {
                                      backgroundColor: (theme) =>
                                        theme.palette.success.main,
                                      color: (theme) =>
                                        theme.palette.success.contrastText,
                                    },
                                  }}
                                >
                                  <TableCell colSpan={5}>
                                    <Typography fontWeight={600} variant="h6">
                                      Optional Increased Accident Benefits
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>

              <Divider sx={{ mt: 1.5, mb: 1.5 }} />
              <Grid
                container
                spacing={2}
                sx={{
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  backgroundColor: (theme) => theme.palette.grey[100],
                }}
              >
                <Grid item xs={9}>
                  <Typography variant="h5" fontWeight={600}>
                    Total Premium
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="h5" fontWeight={600}>
                    $ {total}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography variant="h5" fontWeight={600}>
                    $ {totalTwo}
                  </Typography>
                </Grid>
              </Grid>

              {renderBeforeSave}

              <Box sx={{ mt: 2.5, display: "flex", gap: 2 }}>
                <RoleBasedCustomButton
                  moduleKey={MODULE_IDS.COMMERCIAL_AUTO}
                  sx={{ width: "15%" }}
                  disabled={saveLoading}
                  variant="contained"
                  type="submit"
                >
                  Save
                </RoleBasedCustomButton>
                {renderAfterSave}
              </Box>
            </>
          </FieldSet>
        </form>
      </StandadCard>
    </PageLoader>
    </PageContainer>
  );
};
